import { FileType } from 'shared/enums/FileType';
import { useOptimisticHiddenMedia } from './useOptimisticHiddenMedia';
import {
  IRepresentativeSingleProfileMedia,
  IRrepresentativeShowHiddenMediaPayload,
  ISingleRepresentativeAttachedMediaPayloadResponse,
  ISingleRepresentativeHiddenMediaPayloadResponse,
} from 'shared/interfaces/IRepresentativesEditProfile';
import { useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { MediaFileType } from 'shared/enums/MediaFileType';
import { MediumableType } from 'shared/enums/MediumableType';
import { useQueryClient } from 'react-query';
import { useEditProfileMedia } from '../context/EditProfileMediaProvider';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import { useSnackbar } from 'notistack';

const err_msg = 'An error occurred while processing your request. Your previous sorting has been restored.';

export const useEditProfileMediaHideClick = (
  fileType: FileType,
  profileId: string,
  agencyPermissionValue: AgencyPermissionEnums,
) => {
  const queryClient = useQueryClient();
  const unHideQueueRef = useRef<Set<IRrepresentativeShowHiddenMediaPayload>>(new Set());
  const { handleAction } = useEditProfileMedia();
  const { useOptimisticHideMedia, useOptimisticUnHideMedia } = useOptimisticHiddenMedia();
  const { mutate: hideMedia } = useOptimisticHideMedia(fileType, profileId);
  const { mutate: unhideMedia } = useOptimisticUnHideMedia(fileType, profileId);
  const { enqueueSnackbar } = useSnackbar();

  const onHideClick = (id: string) => {
    handleAction(agencyPermissionValue, () => {
      hideMedia(
        {
          profileId,
          mediaId: id,
          fileType,
        },
        {
          onError: () => {
            enqueueSnackbar(err_msg, {
              variant: 'error',
            });
          },
        },
      );
    });
  };

  const DebounceUnHideClick = useDebouncedCallback(() => {
    const payload: IRrepresentativeShowHiddenMediaPayload[] = Array.from(unHideQueueRef.current);
    unHideQueueRef.current.clear();
    unhideMedia(payload, {
      onError: () => {
        enqueueSnackbar(err_msg, {
          variant: 'error',
        });
      },
    });
  }, 1000);

  const unHideClick = (payload: IRrepresentativeShowHiddenMediaPayload) => {
    handleAction(agencyPermissionValue, () => {
      unHideQueueRef.current.add(payload);

      // The node that will be unhide
      let hiddenNodes: IRepresentativeSingleProfileMedia[] = [];
      // Query Keys
      const attachedMediaQueryKey = [
        REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
        profileId,
        fileType,
      ];
      const hiddenMediaQueryKey = [
        REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media,
        profileId,
        fileType,
      ];

      // Optimistically remove the item that will be unhidden
      queryClient.setQueryData(hiddenMediaQueryKey, (oldData: any) => {
        const prevData: ISingleRepresentativeHiddenMediaPayloadResponse = oldData;

        return {
          ...prevData,
          data: {
            ...prevData.data,
            media_connection: {
              ...prevData?.data.media_connection,
              nodes: prevData?.data?.media_connection?.nodes.filter((node) => {
                if (node.id === payload.id) {
                  const newItem: IRepresentativeSingleProfileMedia = {
                    id: crypto.randomUUID(),
                    primary: false,
                    mediumable_id: crypto.randomUUID(),
                    mediumable_type: MediumableType.PROFILE,
                    sort: 0,
                    medium: {
                      attachment_url: node.attachment_url,
                      modified_attachment_url: node.modified_attachment_url,
                      cropped_attachment_url: node.cropped_attachment_url,
                      id: node.id,
                      file_type: MediaFileType.IMAGE,
                      file_height: '',
                      file_name: `skeleton-optimistic-updating-list-${crypto.randomUUID()}`,
                      file_size: '',
                      file_width: '',
                    },
                  };
                  hiddenNodes = [...hiddenNodes, newItem];
                  return false;
                }
                return true;
              }),
            },
          },
        };
      });

      // Optimistically add the item that will be unhidden in attached media
      queryClient.setQueryData(attachedMediaQueryKey, (oldData: any) => {
        const prevData: ISingleRepresentativeAttachedMediaPayloadResponse = oldData;

        return {
          ...prevData,
          data: {
            ...prevData.data,
            medium_attachments_connection: {
              ...prevData?.data?.medium_attachments_connection,
              nodes: [...(prevData?.data?.medium_attachments_connection?.nodes || []), ...hiddenNodes],
            },
          },
        };
      });

      DebounceUnHideClick();
    });
  };

  return { onHideClick, unHideClick };
};
